/** Movement-first encounter briefs. Campaign76 owns ordering/names; these keyed
 * contracts own articulation intent, materials and attack variations. Units are
 * metres and active seconds. Rigid armor hinges must not replace weighted skin
 * on flexible necks. Existing anatomical sockets83 remain the damage authority. */
const designs = {
  0: [
    'antler',
    '#286f70',
    '#dab775',
    [1, 0, 0, 0],
    ['aimed', 'fan', 'gate'],
    'Five-neck chain, jaw, independent wing fingers and delayed tail',
    'Crest armor closes between volleys',
    'Aim at the crest after its blue charge',
  ],
  1: [
    'tidal',
    '#4d8d99',
    '#e6c38a',
    [0.95, 0.6, 0.18, 2],
    ['paired', 'fan', 'gate'],
    'Six carved arms with shoulder and forearm bearings, paired iris shields and a central lens',
    'Stone iris shields protect the lens during tidal volleys',
    'Iris shields part after the paired tide shots; aim at the amber lens',
  ],
  2: [
    'petal',
    '#745687',
    '#f5aecf',
    [1.08, 0.7, 0.2, 3],
    ['aimed', 'paired', 'fan'],
    'Two independent throats, separate jaws and trailing vertebrae',
    'Only one throat opens per cycle',
    'Follow the glowing crown to its open throat',
  ],
  3: [
    'blade',
    '#526e74',
    '#f5b76f',
    [1.05, 0.4, 0.12, 4],
    ['paired', 'fan', 'gate'],
    'Torso yaw, shoulder scythes, elbows and trailing armored legs',
    'Closed reactor vent and heavy forearm shields',
    'Reactor vents after the scythe charge',
  ],
  5: [
    'lantern',
    '#55477c',
    '#b7f5dc',
    [0.9, 0.7, 0.3, 2],
    ['fan', 'paired', 'gate'],
    'Rigid mantle, separate shell valves and delayed tendril chains',
    'Leader mantle closes during spread fire',
    'Shoot the central mantle when the shells part',
  ],
  6: [
    'prism',
    '#8bbed1',
    '#d4f9ff',
    [1.04, 0.5, 0.2, 4],
    ['paired', 'gate', 'fan'],
    'Faceted shell valves and independent articulated tendrils',
    'Crystalline shell plates',
    'Mantle is exposed after the prism volley',
  ],
  7: [
    'stone',
    '#6b6259',
    '#e8bc78',
    [0.94, 0.3, 0, 3],
    ['aimed', 'fan', 'gate'],
    'Rigid torso slabs, shoulder joints, elbows and grounded knees',
    'Stone body absorbs stray shots',
    'Wait for the sternum fissure to light',
  ],
  8: [
    'crown',
    '#536881',
    '#f2c97a',
    [1.12, 0.7, 0.15, 5],
    ['fan', 'paired', 'gate'],
    'Independent crown fins, armored shoulders and reactor shutters',
    'Shielded reactor between attack bursts',
    'Reactor vent opens after the crown barrage',
  ],
  9: [
    'petal',
    '#8e436c',
    '#f4c58f',
    [0.87, 0.5, 0.28, 3],
    ['fan', 'fan', 'paired'],
    'Separate petal hinges, shell mantle and soft trailing tendrils',
    'Overlapping petals protect the mantle',
    'Petals part after the spore fan',
  ],
  10: [
    'fossil',
    '#9caaa7',
    '#d5f8f1',
    [0.98, 0.7, 0.18, 4],
    ['paired', 'fan', 'paired'],
    'Two independent skeletons with rib cages, jaws and tail chains',
    'Only one rib heart is exposed',
    'Switch to the illuminated rib heart each cycle',
  ],
  12: [
    'gear',
    '#6b7188',
    '#f5ce8c',
    [1.1, 0.6, 0.2, 4],
    ['paired', 'gate', 'fan'],
    'Clockwork shell valves, hinged radial gears and tendril linkages',
    'Mantle shutter and alternating lane walls',
    'Counterattack the mantle after the clockwork salvo',
  ],
  13: [
    'crown',
    '#805c52',
    '#ffb56e',
    [1.03, 0.6, 0.2, 5],
    ['fan', 'gate', 'paired'],
    'Gimballed lens, layered crown blades and independent wing vanes',
    'Lens shutter protects the aperture during ash volleys',
    'Lens aperture opens after the ash fan',
  ],
  14: [
    'fossil',
    '#a39bb9',
    '#dbd6ff',
    [0.96, 0.8, 0.15, 5],
    ['aimed', 'paired', 'gate'],
    'Segmented spine and tail with rigid ribs and independent jaw',
    'Ribs screen the recessed heart',
    'Aim through the rib cage after the charge',
  ],
  15: [
    'blade',
    '#556c81',
    '#a3eff4',
    [0.88, 0.5, 0.18, 3],
    ['paired', 'fan', 'gate'],
    'Separate turbine nacelles, wing panels and intake shutters',
    'Only one armored intake opens',
    'Alternate left and right illuminated intakes',
  ],
  16: [
    'solar',
    '#98724d',
    '#fff0a5',
    [1.06, 0.6, 0.25, 5],
    ['fan', 'gate', 'fan'],
    'Torso reactor, articulated solar vanes, elbows and gauntlets',
    'Reactor shutters close during solar storms',
    'Counterattack the reactor in its cooling window',
  ],
};
for (const [key, value] of Object.entries(designs)) {
  const [motif, shell, trim, travel, patterns, segments, strength, weakness] = value;
  designs[key] = Object.freeze({
    motif,
    shell,
    trim,
    travel: Object.freeze(travel),
    patterns: Object.freeze(patterns),
    segments,
    strength,
    weakness,
  });
}
Object.freeze(designs);

/** Resolve an authored campaign design. Unknown/bonus keys deliberately return
 * null, preserving the base model, lane and attacks for isolated policy callers. */
export function skyBossDesign(key) {
  return Number.isInteger(key) && Object.hasOwn(designs, key) ? designs[key] : null;
}

/** C2 blended design-specific motion offset, in metres/metres per second.
 * Starts at zero after the protected entrance. Every boss keeps its physical
 * lane: no visual scale trick can relocate its weakpoint or attack socket.
 * Caller supplies seconds since entrance ended; invalid/negative time is zero. */
export function skyBossTravel(design, elapsedAfterEntry) {
  const spec = skyBossDesign(design);
  const elapsed = Math.max(0, Number.isFinite(elapsedAfterEntry) ? elapsedAfterEntry : 0);
  if (!spec || design === 0) return { offset: [0, 0, 0], velocity: [0, 0, 0] };
  const u = Math.min(1, elapsed / 2.5);
  const blend = u ** 3 * (10 - 15 * u + 6 * u * u);
  const derivative = elapsed < 2.5 ? (30 * u * u * (1 - u) ** 2) / 2.5 : 0;
  const [rate, lateral, climb, depth] = spec.travel;
  const frequency = (Math.PI * rate) / 5;
  const phase = elapsed * frequency;
  const values = [lateral * Math.sin(phase), climb * Math.sin(phase * 2), depth * Math.sin(phase)];
  const speeds = [
    lateral * frequency * Math.cos(phase),
    climb * 2 * frequency * Math.cos(phase * 2),
    depth * frequency * Math.cos(phase),
  ];
  return {
    offset: values.map((v) => v * blend),
    velocity: speeds.map((v, i) => v * blend + values[i] * derivative),
  };
}
